/**
 * `/student/tutor` — Homework Tutor.
 *
 * Sprint 1 ships only the *shell*. The student picks a practice problem,
 * then chooses one of the four learning-safe Socratic actions defined
 * in `@teacher-ai-hub/shared#SOCRATIC_ACTIONS`. There is intentionally no
 * free-text "ask anything" input here — by the design doc the only
 * affordances allowed are the four hint-style prompts.
 *
 * The agent is not yet wired up; clicking an action just records the
 * intent locally so the UI can demonstrate the gating behaviour.
 */

import { useState } from 'react';
import { useApiClient } from '../../api/ApiClientProvider';
import { useFetch } from '../../api/useFetch';
import { useSession } from '../../auth/SessionProvider';
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from '../../components/States';
import { SocraticActions } from '../../components/SocraticActions';
import {
  SOCRATIC_ACTIONS,
  type SocraticActionId,
} from '@teacher-ai-hub/shared';

export function StudentTutor() {
  const client = useApiClient();
  const { session } = useSession();
  const token = session?.token ?? '';
  const state = useFetch(
    () => client.listTutorProblems(token),
    [client, token],
  );

  const [selectedProblemId, setSelectedProblemId] = useState<string | null>(
    null,
  );
  const [selectedActionId, setSelectedActionId] =
    useState<SocraticActionId | null>(null);

  if (state.status === 'loading') {
    return <LoadingState label="Loading practice problems…" />;
  }
  if (state.status === 'error') {
    return <ErrorState error={state.error} />;
  }
  if (state.data.length === 0) {
    return (
      <EmptyState
        title="No practice problems yet"
        description="The Homework Tutor agent ships in a later sprint. Until then this list pulls from hand-curated mock problems."
      />
    );
  }

  const problems = state.data;
  const activeProblem =
    problems.find((p) => p.id === selectedProblemId) ?? problems[0]!;
  const actionLabel = SOCRATIC_ACTIONS.find(
    (a) => a.id === selectedActionId,
  )?.label;

  return (
    <section className="page" data-testid="student-tutor">
      <header className="page__header">
        <h1>Homework Tutor</h1>
        <p className="callout">
          The tutor coaches you through problems — it will not produce a
          finished solution. Pick a problem, then choose one of the four
          learning-safe actions below.
        </p>
      </header>

      <div className="tutor">
        <aside
          className="tutor__list"
          aria-label="Practice problems"
          data-testid="tutor-problem-list"
        >
          <h2 className="tutor__list-title">Practice problems</h2>
          <ul className="list">
            {problems.map((problem) => {
              const isActive = problem.id === activeProblem.id;
              return (
                <li
                  key={problem.id}
                  className={
                    isActive
                      ? 'list__item list__item--active'
                      : 'list__item'
                  }
                >
                  <button
                    type="button"
                    className="tutor__list-btn"
                    onClick={() => {
                      setSelectedProblemId(problem.id);
                      setSelectedActionId(null);
                    }}
                    aria-current={isActive ? 'true' : undefined}
                    data-testid={`tutor-problem-${problem.id}`}
                  >
                    <span className="tutor__list-title-row">
                      {problem.title}
                    </span>
                    <span className="muted">{problem.courseName}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        <article
          className="tutor__problem"
          aria-labelledby="tutor-problem-heading"
        >
          <h2 id="tutor-problem-heading">{activeProblem.title}</h2>
          <p className="muted">
            {activeProblem.courseName} · subject: {activeProblem.subject}
          </p>
          <p className="tutor__prompt" data-testid="tutor-problem-prompt">
            {activeProblem.prompt}
          </p>

          <h3>Ask the tutor</h3>
          <SocraticActions
            selectedId={selectedActionId}
            onSelect={(id) => setSelectedActionId(id)}
          />

          <p className="muted tutor__pending" data-testid="tutor-pending">
            {actionLabel
              ? `Queued: ${actionLabel}. The Tutor agent ships in a later sprint, so this is a UI preview only.`
              : 'Pick one of the four actions above. The Tutor will never just hand you the finished solution.'}
          </p>
        </article>
      </div>
    </section>
  );
}
